import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../data/usePage.js";
import { createFile, saveContent } from "../api.js";
import { invalidate } from "../data/useFetch.js";
import { NewFileIcon, NewDirIcon } from "../icons.js";
import { defaultOpen, type Collapse } from "./collapse.js";

const MD = /\.(md|markdown)$/i;
const ROOT_HOME = /^(readme|index|home)$/i;
// Directory-index priority — mirrors the server's resolveContent order. The
// first of these present is what a directory URL renders, so only that one is
// the "current" page (else several index files highlight at once).
const ROOT_INDEX = ["Index.md", "README.md", "index.md", "home.md"];
const CONFIG_REPO = "datawiki-config";

/** The file that makes a freshly-created directory persist (git has no empty
 * dirs). In the config repo's collection dirs it's the right per-entity file;
 * everywhere else an index.md. Created empty. (A little hardcoding.) */
function newDirFile(repo: string, parentDir: string): string {
  if (repo === CONFIG_REPO) {
    if (parentDir === "repos") return "remote";
    if (parentDir === "groups") return "members";
    if (parentDir === "databases") return "connection";
  }
  return "index.md";
}

function hrefFor(repo: string, path: string, rootIndex: string): string {
  // Only the file `/` actually renders points at the clean home URL; the other
  // index files (e.g. README.md when Index.md wins) keep their own path so they
  // stay reachable.
  if (path === rootIndex) return `/${repo}/`;
  return `/${repo}/${path.replace(MD, "")}`;
}

interface SNode {
  name: string;
  path: string;
  dir: boolean;
  children: SNode[];
}

function buildTree(files: string[]): SNode {
  const root: SNode = { name: "", path: "", dir: true, children: [] };
  for (const file of files) {
    const parts = file.split("/");
    let node = root;
    parts.forEach((part, i) => {
      const isLeaf = i === parts.length - 1;
      const path = parts.slice(0, i + 1).join("/");
      let child = node.children.find((c) => c.name === part && c.dir === !isLeaf);
      if (!child) {
        child = { name: part, path, dir: !isLeaf, children: [] };
        node.children.push(child);
      }
      node = child;
    });
  }
  // Important files (readme/index/home) first, then directories, then files.
  const rank = (n: SNode): number =>
    !n.dir && ROOT_HOME.test(n.name.replace(MD, "")) ? 0 : n.dir ? 1 : 2;
  const sort = (n: SNode): void => {
    n.children.sort(
      (a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name),
    );
    n.children.forEach(sort);
  };
  sort(root);
  return root;
}

/** The two icon buttons (new file / new directory). */
function AddButtons({
  onPick,
  className,
}: {
  onPick: (mode: "file" | "dir") => void;
  className?: string;
}) {
  // preventDefault stops a click inside <summary> from toggling the <details>.
  const pick = (mode: "file" | "dir") => (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    onPick(mode);
  };
  return (
    <span className={className}>
      <button type="button" className="dw-tree-add-btn" title="new file" aria-label="new file" onClick={pick("file")}>
        <NewFileIcon />
      </button>
      <button type="button" className="dw-tree-add-btn" title="new directory" aria-label="new directory" onClick={pick("dir")}>
        <NewDirIcon />
      </button>
    </span>
  );
}

/** The name box shown after picking +file / +dir. A file is created (empty draft)
 * and opened for editing; a directory has no git identity until it holds a file,
 * so +dir just navigates into the new path — which renders as an empty directory
 * where the first file materializes it. */
function CreateInput({
  repo,
  dir,
  mode,
  onClose,
}: {
  repo: string;
  dir: string;
  mode: "file" | "dir";
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const child = (n: string): string => (dir ? `${dir}/${n}` : n);

  const submit = async (): Promise<void> => {
    const n = name.trim().replace(/^\/+|\/+$/g, "");
    if (!n) return onClose();
    // A new dir has no git identity until it holds a file — materialize it with
    // an empty defining file (index.md, or the config collection's per-entity file).
    const path = mode === "file" ? child(n) : `${child(n)}/${newDirFile(repo, dir)}`;
    try {
      if (mode === "file") {
        await createFile(repo, path);
      } else {
        await saveContent(repo, path, "");
      }
      invalidate("tree");
      invalidate("commit");
      invalidate("session");
      navigate(`/${repo}/${path.replace(/\.(md|markdown)$/, "")}?edit`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
    onClose();
  };

  return (
    <form
      className="dw-tree-create"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <input
        autoFocus
        className="dw-tree-name"
        value={name}
        placeholder={mode === "file" ? "new file name" : "new dir name"}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
      />
    </form>
  );
}

function FolderNode({
  node,
  currentNav,
  rootIndex,
  repo,
  editing,
  collapse,
}: {
  node: SNode;
  currentNav: string;
  rootIndex: string;
  repo: string;
  editing: boolean;
  collapse: Collapse;
}) {
  const [creating, setCreating] = useState<null | "file" | "dir">(null);
  // A persisted user choice (or collapse-all/expand-all) overrides the tree's
  // default open state; the folder still force-opens when the current page
  // lives inside it (or while creating in it), so navigation never hides your
  // place.
  const within = currentNav === node.path || currentNav.startsWith(`${node.path}/`);
  const open = within || creating !== null || collapse.isOpen(node.path, defaultOpen(node.name));
  return (
    <li>
      <details
        open={open}
        onToggle={(e) => {
          // Native <details> flips itself on click; record the intent so it
          // persists. Guard against the echo when we set `open` programmatically.
          const el = e.currentTarget;
          if (el.open !== open) collapse.setOpen(node.path, el.open);
        }}
      >
        <summary>{node.name}</summary>
        {/* Outside <summary> so it only renders while the folder is open. */}
        {editing && <AddButtons className="dw-tree-add" onPick={setCreating} />}
        {creating && (
          <CreateInput repo={repo} dir={node.path} mode={creating} onClose={() => setCreating(null)} />
        )}
        <Nodes nodes={node.children} currentNav={currentNav} rootIndex={rootIndex} repo={repo} editing={editing} collapse={collapse} />
      </details>
    </li>
  );
}

function Nodes({
  nodes,
  currentNav,
  rootIndex,
  repo,
  editing,
  collapse,
}: {
  nodes: SNode[];
  currentNav: string;
  rootIndex: string;
  repo: string;
  editing: boolean;
  collapse: Collapse;
}) {
  return (
    <ul>
      {nodes.map((n) =>
        n.dir ? (
          <FolderNode key={n.path} node={n} currentNav={currentNav} rootIndex={rootIndex} repo={repo} editing={editing} collapse={collapse} />
        ) : (
          (() => {
            const nav = n.path.replace(MD, "");
            const current =
              nav === currentNav ||
              (currentNav === "" && n.path === rootIndex);
            const label = MD.test(n.path) ? n.name.replace(MD, "") : n.name;
            return (
              <li key={n.path}>
                <Link className={`dw-tree-file${current ? " current" : ""}`} to={hrefFor(repo, n.path, rootIndex)}>
                  {label}
                </Link>
              </li>
            );
          })()
        ),
      )}
    </ul>
  );
}

export function Sidebar({
  repo,
  files,
  canEdit,
  currentPath,
  collapse,
}: {
  repo: string;
  files: string[];
  /** May the user create files in this repo (root `edit`, from the tree)? False
   * for a read-only repo like dwdocs, and for one they only have `read` on. */
  canEdit: boolean;
  currentPath: string;
  collapse: Collapse;
}) {
  const session = useSession();
  // Both gates are needed and neither implies the other: `session.editing` is
  // the instance-wide switch plus "signed in at all", `canEdit` is this repo.
  const editing = session.status === "ready" && session.data.editing && canEdit;
  const [rootCreate, setRootCreate] = useState<null | "file" | "dir">(null);
  const currentNav = currentPath.replace(MD, "");
  // Which top-level index file "/" actually renders — the only one to highlight.
  const rootIndex = ROOT_INDEX.find((f) => files.includes(f)) ?? "";

  return (
    <nav className="dw-filetree">
      {editing && (
        <div className="dw-tree-root">
          <span className="dw-tree-root-label">{repo}/</span>
          <AddButtons className="dw-tree-add-inline" onPick={setRootCreate} />
        </div>
      )}
      {editing && rootCreate && (
        <CreateInput repo={repo} dir="" mode={rootCreate} onClose={() => setRootCreate(null)} />
      )}
      {files.length === 0 ? (
        <p className="dw-filetree-empty">No pages yet.</p>
      ) : (
        <Nodes nodes={buildTree(files).children} currentNav={currentNav} rootIndex={rootIndex} repo={repo} editing={editing} collapse={collapse} />
      )}
    </nav>
  );
}
